import React, { useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import {
  createCostCenterGroupApi,
  deleteCostCenterGroupApi,
  CostCenterApiBase,
  DpCostCenterGroup,
  updateCostCenterGroupApi,
} from "../api/settingsApi";

type Props = {
  token: string;
  /** Grupos com os inativos (tela de manutenção). */
  groups: DpCostCenterGroup[];
  /** Recarrega grupos e centros depois de salvar. */
  onChanged: () => Promise<void> | void;
  /** Rota do cadastro: a do DP (padrão) ou a do Financeiro. */
  apiBase?: CostCenterApiBase;
  /** Sem escrita (FINANCEIRO_LEITURA): só a lista, sem botões. */
  canWrite?: boolean;
};

type Form = { name: string; isActive: boolean };
const emptyForm: Form = { name: "", isActive: true };

/**
 * Grupos de centro de custo (DRE por centro de custo, 17/10/2026). Um nível só desde
 * 04/11/2026: grupo → centro de custo (os subgrupos viraram centros de custo). O centro
 * escolhe o grupo no próprio cadastro. Grupo com centro não se exclui: inativa-se.
 */
export default function CostCenterGroupsSettings({ token, groups, onChanged, apiBase = "/dp", canWrite = true }: Props) {
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<DpCostCenterGroup | null>(null);

  const open = (group?: DpCostCenterGroup) => {
    setError(null);
    setForm(group ? { name: group.name, isActive: group.isActive } : emptyForm);
    setModal({ id: group?.id });
  };

  const editing = modal?.id ? groups.find((g) => g.id === modal.id) ?? null : null;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      setError("Informe o nome.");
      return;
    }
    const payload = { name: form.name.trim(), isActive: form.isActive };
    setSaving(true);
    setError(null);
    try {
      if (modal?.id) await updateCostCenterGroupApi(token, modal.id, payload, apiBase);
      else await createCostCenterGroupApi(token, payload, apiBase);
      toast.success("Grupo salvo.");
      setModal(null);
      await onChanged();
    } catch (err: any) {
      setError(err?.message || "Erro ao salvar o grupo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteCostCenterGroupApi(token, removing.id, apiBase);
      toast.success("Grupo excluído.");
      setRemoving(null);
      await onChanged();
    } catch (err: any) {
      toast.error(err?.message || "Erro ao excluir o grupo.");
      setRemoving(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="dpccGroups" aria-labelledby="dpcc-groups-title">
      <div className="dpccGroups__head">
        <h3 id="dpcc-groups-title" className="dpccGroups__title">
          Grupos
        </h3>
        {canWrite ? (
          <button className="dpBtn dpBtn--secondary" onClick={() => open()}>
            Novo grupo
          </button>
        ) : null}
      </div>
      <p className="dpFormHint">
        Organizam os centros de custo na DRE do Financeiro (ex.: grupo 2 Operacional → centro de custo Rio de Janeiro).
        O código e a ordem são gerados pelo sistema, na ordem de cadastro: 1, 2, 3… O grupo de cada centro se escolhe no
        cadastro do centro, abaixo.
      </p>

      <div className="dpTableWrapper">
        <table className="dpTable dpccGroups__table">
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Centros de custo</th>
              <th>Situação</th>
              <th style={{ width: 180 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={4} className="dpEmpty">
                  Nenhum grupo. Sem grupo, a DRE mostra os centros de custo em "Sem grupo".
                </td>
              </tr>
            ) : (
              groups.map((g) => (
                <tr key={g.id}>
                  <td data-label="Grupo">
                    <span className="dpccGroups__name">
                      <span className="dpccGroups__code">{g.code}</span> {g.name}
                    </span>
                  </td>
                  <td data-label="Centros de custo">
                    {g.costCenters.length ? g.costCenters.map((c) => `${c.code} ${c.name}`).join(", ") : "—"}
                  </td>
                  <td data-label="Situação">
                    <span className={`dpBadge ${g.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                      {g.isActive ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td data-label="">
                    {canWrite ? (
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(g)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoving(g)}>
                          Excluir
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Grupo de centro de custo">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.id ? "Editar grupo" : "Novo grupo"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)} disabled={saving} aria-label="Fechar">
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}
                {editing ? (
                  <p className="dpFormHint">
                    Código <strong>{editing.code}</strong> (gerado pelo sistema).
                  </p>
                ) : (
                  <p className="dpFormHint">O código é gerado ao salvar: o próximo número de grupo.</p>
                )}
                <div className="dpFormGroup">
                  <label htmlFor="ccgName">Nome *</label>
                  <input
                    id="ccgName"
                    className="dpInput"
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir grupo?"
        message={
          removing
            ? `"${removing.name}" será excluído. Grupo com centro de custo não se exclui: mova os centros antes, ou inative o grupo.`
            : ""
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
